import { GatsbyImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { InView } from 'react-intersection-observer';
import imgFallback from 'src/utils/image';
import SectionHeader from '../SectionHeader/SectionHeader';
import { getRandomElementFromArray, replaceItemInArray } from 'src/utils/helpers';
import './Partners.scss';

const NUMBER_OF_MAX_VISIBLE_LOGOS = 8;
const CHANGE_LOGO_DELAY_IN_MS = 1500;

function Partners({ partners: { title, tagline, description, logos } }) {
  const [isInView, setIsInView] = useState(false);
  const [visibleLogos, setVisibleLogos] = useState(logos.slice(0, NUMBER_OF_MAX_VISIBLE_LOGOS));
  const [nonVisibleLogos, setNonVisibleLogos] = useState(logos.slice(NUMBER_OF_MAX_VISIBLE_LOGOS));
  const intervalRef = useRef(null);
  const shouldChangeLogos = logos.length > NUMBER_OF_MAX_VISIBLE_LOGOS;

  useEffect(() => {
    function changeItems() {
      const toChange = getRandomElementFromArray(visibleLogos);
      const changeTo = getRandomElementFromArray(nonVisibleLogos);

      const nextVisibleLogos = replaceItemInArray(visibleLogos, visibleLogos.indexOf(toChange), changeTo);

      setVisibleLogos(nextVisibleLogos);

      const nextNonVisibleLogos = replaceItemInArray(nonVisibleLogos, nonVisibleLogos.indexOf(changeTo), toChange);

      setNonVisibleLogos(nextNonVisibleLogos);
    }

    if (shouldChangeLogos && isInView) {
      intervalRef.current = setInterval(changeItems, CHANGE_LOGO_DELAY_IN_MS);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [visibleLogos, nonVisibleLogos, shouldChangeLogos, isInView]);

  return (
    <Container fluid as="section" className="py-5">
      <SectionHeader title={title} tagline={tagline} description={description} />
      <Container>
        <InView onChange={(inView) => setIsInView(inView)}>
          <Row>
            <Col xs={12} className="py-5">
              <Row className="justify-content-center align-items-center">
                {visibleLogos.map((logo, index) => (
                  <Col key={logo?.props?.id || index} xs={6} sm={4} lg={3} className="py-3 text-center">
                    <GatsbyImage
                      id={logo.asset?._key}
                      image={logo?.image?.asset?.gatsbyImageData ?? imgFallback}
                      alt={logo?.alt ?? ''}
                      style={{ maxWidth: '50%', height: '80px' }}
                      imgStyle={{ objectFit: 'contain' }}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </InView>
      </Container>
    </Container>
  );
}

Partners.propTypes = {
  partners: PropTypes.shape({
    title: PropTypes.string.isRequired,
    tagline: PropTypes.string,
    description: PropTypes.string,
    logos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
};

export default Partners;
